import type {
  AttachmentMimeType,
  MessageType,
  MessageValues,
  NotificationChannel,
  NotificationTestFormValues,
  NotificationTestPayload,
  ScheduleValues,
  SelectedFile,
} from '../types/notificationTest';
import { isSupportedAttachmentMime } from '../types/notificationTest';
import { CHANNEL_RECIPIENT_FIELD } from '../constants';
import {
  collectReferencedCids,
  rewriteEditorHtmlForEmail,
} from './rewriteEditorHtml';

type OutgoingAttachment = NonNullable<
  NotificationTestPayload['message']['attachments']
>[number];

const buildAttachments = (
  files: SelectedFile[],
  messageType: MessageType
): OutgoingAttachment[] =>
  files
    .filter((f) => f.base64 !== '' && f.name !== '')
    .filter((f) => isSupportedAttachmentMime(f.type))
    .map((f) => {
      const base: OutgoingAttachment = {
        name: f.name,
        content: f.base64,
        type: f.type as AttachmentMimeType,
      };
      if (messageType === 'CUSTOM' && f.inline === true) {
        const cid = (f.contentId ?? '').trim();
        if (cid !== '') {
          base.disposition = 'inline';
          base.contentId = cid;
        }
      }
      return base;
    });

const pad = (n: number): string => String(n).padStart(2, '0');

const toLocalIso = (date: string, time: string): string => {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
};

const buildVariables = (
  variables: MessageValues['variables']
): Record<string, string> =>
  variables
    .filter((v) => v.key.trim() !== '')
    .reduce<Record<string, string>>((acc, v) => {
      acc[v.key] = v.value;
      return acc;
    }, {});

const buildSchedule = (
  s: ScheduleValues
): NotificationTestPayload['schedule'] => {
  if (s.type === 'NONE') return undefined;

  const result: NonNullable<NotificationTestPayload['schedule']> = {
    type: s.type,
    timezone: s.timezone,
    recurring: {
      interval: {
        value: Number(s.recurring.intervalValue) || 0,
        unit: s.recurring.intervalUnit,
      },
    },
  };

  if (s.date && s.time) {
    result.datetime = toLocalIso(s.date, s.time);
  }
  if (s.recurring.expiresDate && s.recurring.expiresTime) {
    result.recurring.expiresAt = toLocalIso(
      s.recurring.expiresDate,
      s.recurring.expiresTime
    );
  }

  return result;
};

const buildMediaEnvelope = (
  hm: MessageValues['headerMedia'] | MessageValues['customMedia'] | undefined
): NotificationTestPayload['message']['headerMedia'] => {
  if (!hm || hm.type === '') return undefined;
  if (hm.source === 'URL') {
    if ((hm.link ?? '').trim() === '') return undefined;
    return {
      type: hm.type,
      link: hm.link.trim(),
      ...(hm.type === 'DOCUMENT' && hm.fileName
        ? { fileName: hm.fileName }
        : {}),
    };
  }
  if ((hm.fileBase64 ?? '') === '') return undefined;
  return {
    type: hm.type,
    fileBase64: hm.fileBase64,
    mimeType: hm.mimeType,
    ...(hm.fileName ? { fileName: hm.fileName } : {}),
  };
};

const buildMessage = (
  message: MessageValues,
  channel: NotificationChannel,
  attachments: SelectedFile[]
): NotificationTestPayload['message'] => {
  if (message.type === 'TEMPLATE') {
    const headerMedia =
      channel === 'whatsapp' ? buildMediaEnvelope(message.headerMedia) : undefined;
    const headerTextVariables =
      channel === 'whatsapp' ? buildVariables(message.headerTextVariables) : {};
    return {
      type: 'TEMPLATE',
      templateId: message.templateId,
      ...(channel === 'whatsapp' && message.metaTemplateId
        ? { metaTemplateId: message.metaTemplateId }
        : {}),
      templateType: message.templateType,
      // Text channels render the template's plain-text body; only email keeps EMAIL. WhatsApp
      // Templatify (session) sends render to plain text too; the Meta path ignores this field.
      templateReturnType:
        channel === 'sms' || channel === 'telegram' || channel === 'whatsapp'
          ? 'PLAIN_TEXT'
          : 'EMAIL',
      language: message.language,
      variables: buildVariables(message.variables),
      ...(Object.keys(headerTextVariables).length > 0
        ? { headerTextVariables }
        : {}),
      ...(headerMedia ? { headerMedia } : {}),
    };
  }
  const outgoing = buildAttachments(attachments, message.type);
  const content =
    channel === 'email'
      ? rewriteEditorHtmlForEmail(message.content)
      : message.content;
  let finalAttachments = outgoing;
  if (channel === 'email') {
    const referenced = collectReferencedCids(content);
    finalAttachments = outgoing.filter((a) => {
      if (a.disposition === 'inline' && a.contentId) {
        return referenced.has(a.contentId);
      }
      return true;
    });
  }

  const customMedia =
    channel === 'whatsapp' ? buildMediaEnvelope(message.customMedia) : undefined;

  return {
    type: 'CUSTOM',
    subject: message.subject,
    content,
    ...(finalAttachments.length > 0 ? { attachments: finalAttachments } : {}),
    ...(customMedia ? { customMedia } : {}),
  };
};

export const buildTestPayload = (
  channel: NotificationChannel,
  v: NotificationTestFormValues
): NotificationTestPayload => {
  const recipientList = v.recipients.addresses.map((a) => a.value);
  const recipientField = CHANNEL_RECIPIENT_FIELD[channel];
  const recipients = (
    v.recipients.type === 'GROUP'
      ? {
          type: 'GROUP' as const,
          [recipientField]: recipientList,
          groupId: v.recipients.groupId,
        }
      : { type: 'INDIVIDUAL' as const, [recipientField]: recipientList }
  ) as NotificationTestPayload['recipients'];

  const schedule = buildSchedule(v.schedule);

  return {
    accountId: v.accountId,
    appId: v.appId,
    messageType: v.messagePriority,
    webhookUrl: v.webhookUrl,
    recipients,
    message: buildMessage(v.message, channel, v.attachments),
    ...(schedule ? { schedule } : {}),
  };
};
